import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
          <p className="text-gray-600 mb-8">
            Welcome to JobLoop! These Terms and Conditions govern your use of our website and services. 
            By accessing or using our platform, you agree to be bound by these terms. If you do not agree 
            with any part of these terms, you must not use our services.
          </p>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Definitions</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>"Website"</strong> refers to https://jobloop.in/.</li>
              <li><strong>"Services"</strong> refers to the job posting, resume database, and other related services offered on the Website.</li>
              <li><strong>"Employer"</strong> refers to any person or entity using the Services to post job vacancies.</li>
              <li><strong>"Job Seeker"</strong> refers to any person using the Services to apply for jobs.</li>
              <li><strong>"User"</strong> refers to any Employer or Job Seeker using the Website.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. User Accounts</h2>
            <p className="text-gray-700">
              To use certain features, you must create an account. You are responsible for maintaining the 
              confidentiality of your account information and for all activities that occur under your account. 
              You agree to notify us immediately of any unauthorized use of your account.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Responsibilities</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>You must be at least 18 years old to use our Services.</li>
              <li>You are responsible for the accuracy of all information you provide, including job descriptions, personal details, and resumes.</li>
              <li>You agree not to post false, misleading, or fraudulent information.</li>
              <li>You agree not to use the Services for any unlawful purpose or to violate any local, state, or international laws.</li>
              <li>You will not interfere with the proper working of the Website.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Paid Services and Subscription Plans</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.1 Employer Subscription Plans</h3>
                <p className="text-gray-700 mb-2">
                  Employers must subscribe to a paid plan to access certain premium features, including but not limited to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Viewing candidate contact information (phone numbers, email addresses)</li>
                  <li>Unlimited job postings (depending on plan tier)</li>
                  <li>Access to advanced search and filtering in the resume database</li>
                  <li>AI-powered candidate screening and matching</li>
                  <li>Priority customer support</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.2 Access to Candidate Contact Information</h3>
                <p className="text-gray-700">
                  Candidate contact information (including phone numbers and email addresses) is protected and only accessible to employers with an active paid subscription plan. Free tier users and employers without active subscriptions cannot view or access candidate contact details. Employers must maintain an active subscription to continue accessing this information.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.3 Payment Terms</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>All subscription fees are charged in advance and are non-refundable unless otherwise specified in our Refund Policy.</li>
                  <li>Subscriptions automatically renew at the end of each billing cycle unless cancelled prior to renewal.</li>
                  <li>Prices are subject to change with 30 days' notice to existing subscribers.</li>
                  <li>We reserve the right to modify, suspend, or discontinue any subscription plan at any time.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Job Posting and Content Rules for Employers</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Job posts must be genuine and for actual, current vacancies.</li>
              <li>You may not post jobs that are discriminatory, illegal, or sexually explicit.</li>
              <li>You may not use our platform to recruit for multi-level marketing (MLM) schemes or similar programs.</li>
              <li>We reserve the right to remove any job posting at our discretion without a refund if it violates these terms.</li>
              <li>Employers may not scrape, harvest, or otherwise collect candidate contact information for use outside of our platform or in violation of our terms.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-700">
              The content on the Website, including text, graphics, logos, and software, is the property of 
              JobLoop or its licensors and is protected by copyright and other intellectual property laws. 
              You may not copy, reproduce, or distribute any content without our prior written consent.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-gray-700">
              The Services are provided on an "as is" and "as available" basis without any warranties of any kind, 
              either express or implied. We do not guarantee that the Website will be error-free or that the 
              Services will meet your specific needs.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700">
              In no event shall JobLoop, its directors, employees, or agents be liable for any indirect, 
              incidental, special, or consequential damages arising from your use of the Services, even if 
              we have been advised of the possibility of such damages.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify and hold harmless JobLoop from any claims, damages, or expenses arising 
              from your use of the Services or your violation of these terms.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
            <p className="text-gray-700">
              These Terms and Conditions are governed by the laws of India.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to the Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify these terms at any time. Your continued use of the Website after 
              any changes indicates your acceptance of the new terms.
            </p>
          </section>

          {/* Last Updated */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Last Updated: October 29, 2025
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2025 JobLoop. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
